import React, { useState } from 'react';
import { updateProjectProgress, addProjectUpdate } from '../services/api';
import '../styles/ProgressModal.css';

const ProgressModal = ({ payment, onClose, onUpdateProgress, onAddUpdate }) => {
  const [selectedStep, setSelectedStep] = useState(payment.progress?.currentStep || 0);
  const [stepNotes, setStepNotes] = useState('');
  const [updateTitle, setUpdateTitle] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');
  const [activeTab, setActiveTab] = useState('progress'); // progress or updates

  const steps = payment.progress?.steps || [
    { name: 'Paiement', status: 'pending', notes: '' },
    { name: 'Design/Maquette', status: 'pending', notes: '' },
    { name: 'Développement', status: 'pending', notes: '' },
    { name: 'Tests & Validation', status: 'pending', notes: '' },
    { name: 'Livraison', status: 'pending', notes: '' }
  ];

  const stepIcons = ['💳', '🎨', '💻', '✅', '🚀'];

  const handleStepClick = (index) => {
    setSelectedStep(index);
    setStepNotes(steps[index]?.notes || '');
  };

  const handleMarkAsInProgress = () => {
    onUpdateProgress(payment._id, selectedStep, 'in_progress', stepNotes);
  };

  const handleMarkAsCompleted = () => {
    onUpdateProgress(payment._id, selectedStep, 'completed', stepNotes);
  };

  const handleMarkAsPending = () => {
    onUpdateProgress(payment._id, selectedStep, 'pending', stepNotes);
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    
    if (!updateTitle || !updateMessage) {
      alert('Titre et message requis');
      return;
    }

    const success = await onAddUpdate(payment._id, updateTitle, updateMessage);
    
    if (success) {
      setUpdateTitle('');
      setUpdateMessage('');
    }
  };

  const getStepStatusBadge = (status) => {
    const config = {
      pending: { label: 'En attente', className: 'status-pending' },
      in_progress: { label: 'En cours', className: 'status-in-progress' },
      completed: { label: 'Terminé', className: 'status-completed' }
    };
    const conf = config[status] || config.pending;
    return <span className={`step-status-badge ${conf.className}`}>{conf.label}</span>;
  };

  return (
    <div className="progress-modal-overlay" onClick={onClose}>
      <div className="progress-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="progress-modal-header">
          <div>
            <h2>📊 Gestion du projet</h2>
            <p className="project-name">{payment.projectName} - {payment.clientName}</p>
          </div>
          <button onClick={onClose} className="close-modal-btn">✕</button>
        </div>

        {/* Tabs */}
        <div className="progress-tabs">
          <button
            className={`progress-tab ${activeTab === 'progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('progress')}
          >
            📈 Progression
          </button>
          <button
            className={`progress-tab ${activeTab === 'updates' ? 'active' : ''}`}
            onClick={() => setActiveTab('updates')}
          >
            📢 Mises à jour
          </button>
          <button
            className="progress-tab"
            onClick={() => window.open(`/order/${payment._id}`, '_blank')}
          >
            👁️ Voir comme client
          </button>
        </div>

        {/* Tab: Progression */}
        {activeTab === 'progress' && (
          <div className="progress-tab-content">
            <div className="progress-grid">
              {/* Liste des étapes */}
              <div className="steps-list">
                <h3>Étapes du projet</h3>
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`step-item ${selectedStep === index ? 'selected' : ''} ${step.status}`}
                    onClick={() => handleStepClick(index)}
                  >
                    <span className="step-item-icon">{stepIcons[index]}</span>
                    <div className="step-item-info">
                      <span className="step-item-name">{step.name}</span>
                      {getStepStatusBadge(step.status)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Détails de l'étape sélectionnée */}
              <div className="step-details">
                <div className="step-details-header">
                  <span className="step-details-icon">{stepIcons[selectedStep]}</span>
                  <div>
                    <h3>{steps[selectedStep]?.name}</h3>
                    {getStepStatusBadge(steps[selectedStep]?.status)}
                  </div>
                </div>

                {steps[selectedStep]?.completedAt && (
                  <div className="step-completed-info">
                    ✓ Terminé le {new Date(steps[selectedStep].completedAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}

                <div className="step-notes-section">
                  <label>Notes / Informations pour le client</label>
                  <textarea
                    value={stepNotes}
                    onChange={(e) => setStepNotes(e.target.value)}
                    placeholder="Ex: La maquette est prête, en attente de validation..."
                    rows="4"
                  />
                </div>

                <div className="step-actions">
                  <button
                    onClick={handleMarkAsPending}
                    className="step-action-btn pending-btn"
                    disabled={steps[selectedStep]?.status === 'pending'}
                  >
                    ⏸️ En attente
                  </button>
                  <button
                    onClick={handleMarkAsInProgress}
                    className="step-action-btn progress-btn"
                    disabled={steps[selectedStep]?.status === 'in_progress'}
                  >
                    ▶️ En cours
                  </button>
                  <button
                    onClick={handleMarkAsCompleted}
                    className="step-action-btn completed-btn"
                    disabled={steps[selectedStep]?.status === 'completed'}
                  >
                    ✅ Terminé
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Mises à jour */}
        {activeTab === 'updates' && (
          <div className="progress-tab-content">
            <div className="updates-container">
              <div className="add-update-form">
                <h3>Ajouter une mise à jour</h3>
                <form onSubmit={handleSubmitUpdate}>
                  <div className="form-group">
                    <label>Titre *</label>
                    <input
                      type="text"
                      value={updateTitle}
                      onChange={(e) => setUpdateTitle(e.target.value)}
                      placeholder="Ex: Maquette validée !"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Message *</label>
                    <textarea
                      value={updateMessage}
                      onChange={(e) => setUpdateMessage(e.target.value)}
                      placeholder="Ex: La maquette a été validée, je passe au développement..."
                      rows="4"
                      required
                    />
                  </div>
                  <button type="submit" className="submit-update-btn">
                    📤 Envoyer la mise à jour
                  </button>
                </form>
              </div>

              <div className="updates-history">
                <h3>Historique des mises à jour</h3>
                {payment.updates && payment.updates.length > 0 ? (
                  <div className="updates-list">
                    {payment.updates.slice().reverse().map((update, index) => (
                      <div key={index} className="update-card">
                        <div className="update-card-header">
                          <h4>{update.title}</h4>
                          <span className="update-date">
                            {new Date(update.createdAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="update-message">{update.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-updates">
                    Aucune mise à jour pour le moment
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressModal;